import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld, cellCenter, heroWalkable } from '../grid.ts';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc, SlideRules, SlideState } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { laneClear, laneFlow } from './wedge-layout.ts';
import { standBy, standDone } from './wedges.ts';

/**
 * The avalanche's slopes (docs/sendas/guerrero.md, chapter 4, «El alud»; the grounds in battle/slide-layout.ts).
 *
 * The front (acts 1 and 3): the snow comes down the slope as a line, its white shadow on the snow a while before. What
 * it catches out in the open it strikes and pushes down the slope; the cells straight below a rock (its lee) it misses.
 * A common nightmare it sweeps away; a giant stands it.
 *
 * - The loose slope (act 1): Kel hauls the stretcher up the trail to the ice cave, stopping while a nightmare is on
 *   him or the hero lags, and running for a rock's lee when a front's shadow shows. Three fronts; wolves; a small giant
 *   asleep beside the trail. Kel at the cave's mouth wins it.
 * - The large wedges (act 3, the test; the puzzle in battle/wedge-layout.ts): seven lanes, nine rocks with their grain,
 *   four iron wedges driven in or pulled standing still by a rock (the wedges' hands, battle/wedges.ts). The avalanche
 *   comes down each lane as the wedges turn it; at the foot, the cave's mouth: clean, it is won; touched once, the
 *   stretcher holds (a shield lost) and it comes down again; twice, lost. The giant walks across the slope throwing
 *   rocks at the hero; wolves come over the edges.
 *
 * Cost: a front lives a couple of seconds and looks at each nightmare once a tick while it does (a field of tens); the
 * lanes' flow is nine rocks worked out again only while a front is down.
 */

const fresh = (s: BattleState, r: SlideRules): SlideState => ({
  t0: s.tick,
  on: false,
  y: r.top,
  n: 0,
  next: s.tick + (r.loose ? (r.loose.fronts[0] ?? 0) : (r.lanes?.fall ?? 0)),
  struck: [],
  way: 1,
  hiding: false,
  lee: -1,
  giant: 0,
  awake: false,
  shut: r.lanes ? r.lanes.rows.flatMap((row) => row.rocks.map(() => 0)) : [],
  hand: r.lanes?.wedges ?? 0,
  at: -1,
  held: 0,
  touches: 0,
  lost: 0,
  split: false,
  over: -1,
  leg: 1,
  stones: [],
  throwAt: s.tick + (r.lanes?.stones.every ?? 0),
});

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

const binHas = (a: number[], v: number): boolean => {
  let lo = 0;
  let hi = a.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = a[m] as number;
    if (x === v) return true;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return false;
};

/** The rocks of the large wedges in one list (row by row), with where each one stands. */
export const slideRocks = (r: SlideRules): { lane: number; dir: number; x: number; y: number }[] => (r.lanes ? r.lanes.rows.flatMap((row) => row.rocks) : []);

/** Which lane a point is in (−1 off the slope). */
export const laneAt = (r: SlideRules, x: number): number => {
  const L = r.lanes;
  if (!L) return -1;
  const k = idiv(x - L.x0, L.laneW);
  return x < L.x0 || k >= 7 ? -1 : k;
};

/** How thick the snow is where it comes down at this point (1 everywhere on the loose slope; by lane on the large one). */
export const snowAt = (r: SlideRules, w: SlideState, x: number, y: number, seg?: number[][]): number => {
  const L = r.lanes;
  if (!L) return 1;
  const k = laneAt(r, x);
  if (k < 0) return 0;
  let n = 0;
  for (const row of L.rows) if (row.y < y) n++;
  const flow = seg ?? laneFlow(L.rows.map((x) => x.rocks), w.shut).seg;
  return (flow[n] as number[])[k] as number;
};

/** In a rock's lee, or where no snow comes down: the front misses it. */
const sheltered = (s: BattleState, r: SlideRules, w: SlideState, x: number, y: number, seg?: number[][]): boolean =>
  binHas(r.lee, cellAtWorld(s, x, y)) || snowAt(r, w, x, y, seg) <= 0;

/** Down the slope `push`, to the farthest open ground on the way (never into rock or off the board). */
const pushDown = (s: BattleState, r: SlideRules, o: { x: number; y: number }): void => {
  for (let d = r.push; d > 0; d -= 433) {
    const c = cellAtWorld(s, o.x, o.y + d);
    if (c >= 0 && heroWalkable(s, c)) {
      o.y += d;
      return;
    }
  }
};

/** The front's line from `py` to `y`: what it crosses in the open, it strikes (once a front). */
const sweep = (s: BattleState, r: SlideRules, w: SlideState, py: number, y: number): void => {
  const seg = r.lanes ? laneFlow(r.lanes.rows.map((x) => x.rocks), w.shut).seg : undefined;
  const crossed = (oy: number): boolean => oy > py && oy <= y;
  s.heroes.forEach((h, i) => {
    if (h.downTicks > 0 || !crossed(h.y) || w.struck.includes(-1 - i) || sheltered(s, r, w, h.x, h.y, seg)) return;
    w.struck.push(-1 - i);
    damageHero(s, h, mulBp(h.stats.maxHp, r.heroBp), null, 'physical', true);
    pushDown(s, r, h);
    h.destX = h.ax = h.x;
    h.destY = h.ay = h.y;
    h.path = [];
    h.moving = false;
    h.stun = imax(h.stun, r.stun);
    ev(s, { t: 'senda', kind: 'swept', npc: '', x: h.x, y: h.y });
  });
  const people = s.senda?.npcs ?? [];
  const defs = s.rules?.senda?.npcs ?? [];
  people.forEach((n, i) => {
    if (n.hp <= 0 || defs[i]?.safe || !crossed(n.y) || w.struck.includes(-100 - i) || sheltered(s, r, w, n.x, n.y, seg)) return;
    w.struck.push(-100 - i);
    n.hp = imax(0, n.hp - r.blows);
    pushDown(s, r, n);
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'swept' : 'down', npc: n.id, x: n.x, y: n.y });
  });
  const pid = s.heroes[0]?.pid;
  for (const e of s.enemies) {
    if (e.hp <= 0 || NIGHTMARES[e.kind].big || !crossed(e.y) || sheltered(s, r, w, e.x, e.y, seg)) continue;
    e.hp = 0;
    if (pid) e.lastPid = pid;
    ev(s, { t: 'senda', kind: 'swept', npc: '', x: e.x, y: e.y, n: e.id });
  }
};

/** A front's turn: due, it starts down from the top; down, it moves and strikes; past the foot, `end`. */
const frontTick = (s: BattleState, r: SlideRules, w: SlideState, cue: (c: string) => void, end: () => void): void => {
  if (!w.on && w.next >= 0 && s.tick >= w.next) {
    w.on = true;
    w.y = r.top;
    w.struck = [];
    w.n++;
    ev(s, { t: 'senda', kind: 'front', npc: '', x: 0, y: r.top, n: w.n });
    cue(`front${w.n}`);
  }
  if (!w.on) return;
  const py = w.y;
  w.y = imin(r.foot, w.y + r.speed);
  sweep(s, r, w, py, w.y);
  if (w.y >= r.foot) {
    w.on = false;
    end();
  }
};

/** A front's shadow is on the snow (or the front itself is coming down). */
export const slideShadow = (s: BattleState, r: SlideRules, w: SlideState): boolean => w.on || (w.next >= 0 && s.tick >= w.next - r.warn);

/* ---------- the loose slope ---------- */

/** The nearest rock's lee to a point (a cell; −1 none within four hexes). */
const nearestLee = (s: BattleState, r: SlideRules, x: number, y: number): number => {
  let best = -1;
  let bd = 4000 * 4000;
  for (const c of r.lee) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(s.map.radius, c);
    const d = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
    if (d < bd) {
      bd = d;
      best = c;
    }
  }
  return best;
};

const looseTick = (s: BattleState, r: SlideRules, w: SlideState, cue: (c: string) => void): void => {
  const L = r.loose;
  if (!L) return;
  if (!w.giant) {
    const e = spawnEnemy(s, 'giant', 1, false, 0, L.giant.x, L.giant.y, L.giantHp);
    e.damage = mulBp(e.damage, L.giantDmgBp);
    e.facing = 256;
    w.giant = e.id;
  }
  frontTick(s, r, w, cue, () => {
    const t = L.fronts[w.n];
    w.next = t === undefined ? -1 : w.t0 + t;
  });
  const kel = npcOf(s, 'kel');
  const h = s.heroes.find((x) => x.downTicks === 0);
  const g = findEnemy(s, w.giant);
  if (g && g.hp > 0 && !w.awake && ((h && withinRange(h.x, h.y, g.x, g.y, L.wake)) || (kel && withinRange(kel.x, kel.y, g.x, g.y, L.wake)) || g.hp < g.maxHp)) {
    w.awake = true;
    ev(s, { t: 'senda', kind: 'wake', npc: '', x: g.x, y: g.y });
    cue('giant');
  }
  if (!kel) return;
  kel.moving = false;
  // A front's shadow on the snow: he runs for the nearest rock's lee and waits there until it has passed.
  if (slideShadow(s, r, w)) {
    if (!w.hiding) {
      w.hiding = true;
      w.lee = nearestLee(s, r, kel.x, kel.y);
      cue('shadow');
    }
    if (w.lee >= 0) {
      const p = cellCenter(s.map.radius, w.lee);
      if (!withinRange(kel.x, kel.y, p.x, p.y, 60)) {
        kel.moving = true;
        stepToward(kel, p.x, p.y, L.pace * 2);
      }
    }
    follow(s, kel);
    return;
  }
  w.hiding = false;
  w.lee = -1;
  // He waits for the hero, and while a nightmare is on him (the giant asleep doesn't count).
  if (!h || !withinRange(h.x, h.y, kel.x, kel.y, L.wait)) return follow(s, kel);
  for (const e of s.enemies) if (e.hp > 0 && (e.id !== w.giant || w.awake) && withinRange(e.x, e.y, kel.x, kel.y, L.halt)) return follow(s, kel);
  const p = L.way[w.way];
  if (p) {
    kel.moving = true;
    if (stepToward(kel, p.x, p.y, L.pace)) w.way = imin(L.way.length - 1, w.way + 1);
  }
  follow(s, kel);
};

/** The mule with the stretcher and Dalmo come up behind Kel, on the trail's side. */
const follow = (s: BattleState, kel: SendaNpc): void => {
  const mule = npcOf(s, 'mule');
  const dalmo = npcOf(s, 'dalmo');
  const go = (n: SendaNpc | undefined, x: number, y: number): void => {
    if (!n) return;
    n.moving = false;
    if (withinRange(n.x, n.y, x, y, 200)) return;
    n.moving = true;
    stepToward(n, x, y, 45);
  };
  go(mule, kel.x - 300, kel.y + 900);
  go(dalmo, kel.x + 600, kel.y + 1300);
};

/* ---------- the large wedges ---------- */

/** The hero sent to a rock (a tap on its mark): it walks below it and, standing still there, drives a wedge in or pulls it. */
export const slideOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  const rk = r ? slideRocks(r)[at] : undefined;
  if (!r || !w || !rk) return;
  if (w.shut[at] !== 1 && w.hand <= 0) return;
  w.at = at;
  w.held = 0;
  orderMove(s, h, rk.x, rk.y + 700);
  h.target = 0;
  h.forced = false;
};

const lanesTick = (s: BattleState, r: SlideRules, w: SlideState, cue: (c: string) => void): void => {
  const L = r.lanes;
  if (!L) return;
  if (!w.giant) {
    const a = L.giant[0];
    const e = spawnEnemy(s, 'giant', 1, false, 0, a.x, a.y, L.giantHp);
    e.damage = mulBp(e.damage, L.giantDmgBp);
    w.giant = e.id;
    w.awake = true;
  }
  if (w.over < 0)
    frontTick(s, r, w, cue, () => {
      const f = laneFlow(L.rows.map((x) => x.rocks), w.shut);
      if (laneClear(f.seg[f.seg.length - 1] as number[])) {
        w.split = true;
        w.over = s.tick + 36;
        ev(s, { t: 'senda', kind: 'split', npc: '', x: L.mouth.x, y: L.mouth.y });
        cue('split');
        return;
      }
      w.touches++;
      ev(s, { t: 'senda', kind: 'touch', npc: '', x: L.mouth.x, y: L.mouth.y, n: w.touches });
      ev(s, { t: 'boom', x: L.mouth.x, y: L.mouth.y, r: 2200, kind: 'cornice' });
      cue(`touch${w.touches}`);
      if (w.touches >= L.touches) {
        w.over = s.tick + 24;
        return;
      }
      w.lost = 1;
      (s.senda as NonNullable<BattleState['senda']>).nums.shieldLost = 1;
      w.next = s.tick + L.again;
    });
  // The hero's hands at a rock.
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && w.at >= 0) {
    const rk = slideRocks(r)[w.at];
    if (rk && standBy(h, w, rk.x, rk.y, L.reach)) {
      const i = w.at;
      if (w.shut[i] !== 1 && w.hand <= 0) {
        w.at = -1;
        w.held = 0;
      } else if (standDone(w, L.stand)) {
        const first = w.hand === L.wedges && w.shut.every((x) => x === 0);
        if (w.shut[i] === 1) {
          w.shut[i] = 0;
          w.hand++;
          ev(s, { t: 'senda', kind: 'pull', npc: '', x: rk.x, y: rk.y, n: i });
        } else {
          w.shut[i] = 1;
          w.hand--;
          ev(s, { t: 'senda', kind: 'plant', npc: '', x: rk.x, y: rk.y, n: i });
          if (first) cue('plant');
        }
      }
    }
  }
  // The giant's rocks: thrown at the hero from across the slope, their shadow first.
  const g = findEnemy(s, w.giant);
  if (g && g.hp > 0 && h && s.tick >= w.throwAt && !withinRange(g.x, g.y, h.x, h.y, 1500)) {
    w.throwAt = s.tick + L.stones.every;
    const x = h.x + rngRange(s.rng, -500, 500);
    const y = h.y + rngRange(s.rng, -400, 400);
    w.stones.push({ x, y, t: s.tick + L.stones.warn });
    g.anim = 'attack';
    g.animT = 0;
    ev(s, { t: 'senda', kind: 'rockWarn', npc: '', x, y, n: L.stones.warn });
  }
  if (w.stones.length && (w.stones[0] as { t: number }).t <= s.tick) {
    const keep: SlideState['stones'] = [];
    for (const st of w.stones) {
      if (st.t > s.tick) {
        keep.push(st);
        continue;
      }
      ev(s, { t: 'senda', kind: 'rock', npc: '', x: st.x, y: st.y });
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, st.x, st.y, L.stones.r)) damageHero(s, x, mulBp(x.stats.maxHp, L.stones.heroBp), null, 'physical', true);
    }
    w.stones = keep;
  }
};

/**
 * The giants' turn (true: nothing else for it): the loose slope's asleep until it wakes; the large wedges' walks across
 * the slope, end to end, throwing its rocks (lanesTick), and never stops to fight: a hero at it only slows the work.
 */
export const slideHold = (s: BattleState, e: Enemy, pace: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  if (!r || !w || e.id !== w.giant) return false;
  if (r.loose) {
    if (w.awake) return false;
    e.anim = 'idle';
    return true;
  }
  const L = r.lanes;
  if (!L) return false;
  if (e.stun > 0) {
    e.stun--;
    e.anim = 'stun';
    return true;
  }
  if (e.cd > 0) e.cd--;
  const to = L.giant[w.leg] as { x: number; y: number };
  e.anim = 'walk';
  if (stepToward(e, to.x, to.y, pace(e))) w.leg = 1 - w.leg;
  return true;
};

export const slideTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.slide;
  const st = s.senda;
  if (!r || !st || !s.started) return;
  const w = (st.slide ??= fresh(s, r));
  if (r.loose) looseTick(s, r, w, cue);
  else lanesTick(s, r, w, cue);
};

/** The loose slope: Kel at the cave's mouth wins it. The large wedges: the mouth left clean, or touched too often. */
export const slideResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  if (!r || !w) return null;
  if (r.loose) {
    const kel = npcOf(s, 'kel');
    return kel && withinRange(kel.x, kel.y, r.loose.mouth.x, r.loose.mouth.y, r.loose.reach) ? 'won' : null;
  }
  if (w.over >= 0 && s.tick >= w.over) return w.split ? 'won' : 'lost';
  return null;
};

/** How far along: Kel up the way; or the fight's clock to the avalanche. */
export const slideGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.loose) return { done: w.way, need: imax(1, r.loose.way.length - 1) };
  return { done: imin(r.lanes?.fall ?? 1, s.tick - w.t0), need: r.lanes?.fall ?? 1 };
};
